import React, { useState, useEffect, useCallback } from 'react';
import { exportToPdf } from '../utils/exportToPdf';
import { syncDateRange } from '../utils/dateUtils';

// Front Office → Door Audit
// Browses door-opening events streamed from the lock vendor (Phase 3 of the
// lock integration). Filters by date / room / reservation / event_kind.
// CSV + PDF export.
export default function PmsDoorAudit({ token, selectedProperty }) {
  const today = new Date().toISOString().slice(0, 10);
  const fromDefault = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().slice(0, 10);
  })();

  const [filters, setFilters] = useState({
    from_date: fromDefault,
    to_date: today,
    room_number: '',
    reservation_id: '',
    event_kind: ''
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`, 'Content-Type': 'application/json'
  }), [token]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const q = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) q.append(k, v); });
      const res = await fetch(`/api/v1/lock/events?${q.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load events');
      setEvents(data.events || []);
    } catch (e) {
      setError(e.message || 'Load failed');
    } finally { setLoading(false); }
  }, [filters, headers]);

  useEffect(() => { load(); /* initial load */ }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // syncDateRange keeps the pair consistent — picking a From after the To (or a
  // To before the From) drags the other end along instead of leaving an
  // inverted range that silently returns no rows.
  const setF = (k, v) => setFilters((f) => syncDateRange({ ...f, [k]: v }, 'from_date', 'to_date', k));

  const exportCsv = () => {
    if (events.length === 0) return;
    const rows = [
      ['Timestamp', 'Business date', 'Room', 'Event', 'Reject reason', 'Way',
       'Card serial', 'Copy', 'Master user', 'Reservation', 'Peripheral'],
      ...events.map((e) => [
        e.event_at,
        e.business_date,
        e.room_number || '',
        e.event_kind,
        e.reject_reason_text || (e.reject_reason ?? ''),
        e.way || '',
        e.card_serial || '',
        e.copy_no || '',
        e.master_user || '',
        e.reservation_id || '',
        e.peripheral_id
      ])
    ];
    const csv = rows.map((r) =>
      r.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `door-audit_${filters.from_date}_to_${filters.to_date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportPdf = () => {
    if (events.length === 0) return;
    exportToPdf({
      title: 'Door Audit',
      subtitle: `${selectedProperty?.name || ''}  ·  ${filters.from_date} → ${filters.to_date}`,
      meta: [`Generated: ${new Date().toLocaleString()}`, `${events.length} events`],
      orientation: 'landscape',
      filename: `door-audit_${filters.from_date}_to_${filters.to_date}.pdf`,
      compact: true,
      print: true,
      sections: [{
        head: [['Time', 'Room', 'Event', 'Reject', 'Way', 'Card', 'Copy', 'User', 'Periph']],
        body: events.map((e) => [
          new Date(e.event_at).toLocaleString(),
          e.room_number || '—',
          e.event_kind,
          e.reject_reason_text || (e.reject_reason ?? ''),
          e.way || '',
          e.card_serial || '',
          e.copy_no || '',
          e.master_user || '',
          e.peripheral_id
        ])
      }]
    });
  };

  return (
    <div style={{ padding: 24, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b' }}>Door Audit</h2>
          <div style={{ color: '#64748b', fontSize: '0.875rem', marginTop: 4 }}>
            Door-opening events from the on-premise lock system
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportCsv} disabled={events.length === 0} style={btnSecondary}>Export CSV</button>
          <button onClick={exportPdf} disabled={events.length === 0} style={btnSecondary}>Export PDF</button>
          <button onClick={load} disabled={loading} style={btnPrimary}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      <div style={{
        background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
        padding: 14, marginBottom: 12, display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10
      }}>
        <div>
          <label style={lbl}>From date</label>
          <input style={inp} type="date" value={filters.from_date} onChange={(e) => setF('from_date', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>To date</label>
          <input style={inp} type="date" value={filters.to_date} onChange={(e) => setF('to_date', e.target.value)} />
        </div>
        <div>
          <label style={lbl}>Room</label>
          <input style={inp} value={filters.room_number} onChange={(e) => setF('room_number', e.target.value)} placeholder="e.g. 101" />
        </div>
        <div>
          <label style={lbl}>Reservation #</label>
          <input style={inp} value={filters.reservation_id} onChange={(e) => setF('reservation_id', e.target.value)} placeholder="reservation_id" />
        </div>
        <div>
          <label style={lbl}>Event kind</label>
          <select style={inp} value={filters.event_kind} onChange={(e) => setF('event_kind', e.target.value)}>
            <option value="">All</option>
            <option value="OPEN">Opened</option>
            <option value="REJECTED">Rejected</option>
            <option value="SPECIAL">Special</option>
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'end' }}>
          <button onClick={load} disabled={loading} style={{ ...btnPrimary, width: '100%' }}>Apply filters</button>
        </div>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      {events.length === 0 && !loading ? (
        <div style={{
          background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8,
          padding: 40, textAlign: 'center', color: '#475569'
        }}>
          No door events for the selected filters. (Phase 3 starts populating when the lock bridge agent polls WF/WN.)
        </div>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead style={{ background: '#f8fafc', position: 'sticky', top: 0 }}>
              <tr>
                <th style={th}>Time</th>
                <th style={th}>Room</th>
                <th style={th}>Event</th>
                <th style={th}>Reject</th>
                <th style={th}>Way</th>
                <th style={th}>Card</th>
                <th style={th}>Copy</th>
                <th style={th}>Master user</th>
                <th style={th}>Periph</th>
                <th style={th}>Reservation</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.event_id} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={td}>{new Date(e.event_at).toLocaleString()}</td>
                  <td style={td}>{e.room_number || '—'}</td>
                  <td style={td}>
                    <span style={pill(e.event_kind)}>{e.event_kind}</span>
                  </td>
                  <td style={td}>{e.reject_reason_text || (e.reject_reason ?? '')}</td>
                  <td style={td}>{e.way || ''}</td>
                  <td style={td}><code style={{ fontSize: '0.78rem' }}>{e.card_serial || ''}</code></td>
                  <td style={td}>{e.copy_no || ''}</td>
                  <td style={td}>{e.master_user || ''}</td>
                  <td style={td}>{e.peripheral_id}</td>
                  <td style={td}>{e.reservation_id || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function pill(kind) {
  const c = ({
    OPEN:     { bg: '#dcfce7', fg: '#166534' },
    REJECTED: { bg: '#fee2e2', fg: '#991b1b' },
    SPECIAL:  { bg: '#dbeafe', fg: '#1e40af' }
  }[kind] || { bg: '#f1f5f9', fg: '#475569' });
  return {
    background: c.bg, color: c.fg, padding: '2px 8px', borderRadius: 999,
    fontSize: '0.72rem', fontWeight: 600
  };
}

const th = { padding: '8px 10px', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 };
const td = { padding: '8px 10px', color: '#0f172a' };
const lbl = { display: 'block', color: '#475569', fontSize: '0.75rem', fontWeight: 500, marginBottom: 4 };
const inp = { padding: '7px 10px', borderRadius: 6, border: '1px solid #cbd5e1', fontSize: '0.9rem', width: '100%', boxSizing: 'border-box' };
const btnPrimary   = { background: '#2563eb', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 6, fontWeight: 600, cursor: 'pointer' };
const btnSecondary = { background: '#f1f5f9', color: '#0f172a', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' };
